import React, { useState, useEffect, useRef } from 'react';
import { useKeyPressCounter } from '../../hooks/useKeyPressCounter';
import './TheWorldPage.css';

interface TheWorldPageProps {
  isVisible: boolean;
  onScrollLockChange?: (isLocked: boolean) => void;
}

const TheWorldPage: React.FC<TheWorldPageProps> = ({ 
  isVisible, 
  onScrollLockChange 
}) => {
  const [isTextCentered] = useState(true); // 最初から中央に表示（setter未使用のため省略）
  const [isScrollLocked, setIsScrollLocked] = useState(true); // 最初からロック
  const [keyPressCount, setKeyPressCount] = useState(0);
  const [showHint] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  // スペースキー5回でスクロール解除
  const { currentCount } = useKeyPressCounter(
    { targetKey: 'Space', requiredCount: 5, resetTimeoutMs: 3000 },
    () => {
      if (isScrollLocked) {
        setIsScrollLocked(false);
        onScrollLockChange?.(false);
        console.log('スクロールロック解除！');
      }
    }
  );

  // キーカウント更新
  useEffect(() => {
    setKeyPressCount(currentCount);
  }, [currentCount]);

  // ページが表示されたらスクロールをロック
  useEffect(() => {
    if (isVisible) {
      setIsScrollLocked(true);
      onScrollLockChange?.(true);
      // より強固なスクロール無効化
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      // ホイールイベントを無効化
      const preventScroll = (e: WheelEvent) => e.preventDefault();
      const preventTouchMove = (e: TouchEvent) => e.preventDefault();
      const preventKeyScroll = (e: KeyboardEvent) => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
          e.preventDefault();
        }
      };
      
      window.addEventListener('wheel', preventScroll, { passive: false });
      window.addEventListener('touchmove', preventTouchMove, { passive: false });
      window.addEventListener('keydown', preventKeyScroll);
      
      console.log('ザ・ワールドページ：完全スクロールロック開始！');
      
      // クリーンアップ関数を返す
      return () => {
        window.removeEventListener('wheel', preventScroll);
        window.removeEventListener('touchmove', preventTouchMove);
        window.removeEventListener('keydown', preventKeyScroll);
      };
    }
  }, [isVisible, onScrollLockChange]);

  // スクロールロック解除時の処理
  useEffect(() => {
    if (!isScrollLocked) {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      console.log('ザ・ワールドページ：スクロールロック解除！');
    }
  }, [isScrollLocked]);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`the-world-container ${isVisible ? 'visible' : ''}`}
    >
      {/* 背景装飾 */}
      <div className="world-background">
        <div className="geometric-shapes">
          {Array.from({ length: 15 }, (_, i) => (
            <div
              key={i}
              className={`shape shape-${i % 3}`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="world-content">
        <div 
          ref={textRef}
          className={`world-title-container ${isTextCentered ? 'centered' : ''}`}
        >
          <h1 className="world-title">
            <span className="world-text-part">ザ・ワールド</span>
          </h1>
          
          {isTextCentered && (
            <div className="world-subtitle">
              <p>ここで物語が始まる</p>
            </div>
          )}
        </div>
      </div>

      {/* スクロールロック時のUI - 非表示 */}
      {isScrollLocked && false && (
        <div className="scroll-lock-overlay">
          <div className="lock-indicator">
            <div className="lock-icon">🔒</div>
            <div className="lock-message">
              <p>スクロールがロックされました</p>
              {showHint && (
                <div className="unlock-hint">
                  <p>スペースキーを5回押して解除</p>
                  <div className="key-counter">
                    <div className="key-progress">
                      {Array.from({ length: 5 }, (_, i) => (
                        <div
                          key={i}
                          className={`key-dot ${i < keyPressCount ? 'active' : ''}`}
                        />
                      ))}
                    </div>
                    <p className="counter-text">{keyPressCount}/5</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 進行状況表示は削除 */}
    </div>
  );
};

export default TheWorldPage;